import * as React from 'react';

import ActionButton from 'components/buttons/action-button/ActionButton';

import ContentCardContext from 'components/cards/content-card/context/ContentCardContext';
import ContentCard from 'components/cards/content-card/ContentCard';

import { __t, applyTranslations } from 'utils/language';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import './content-cards.scss';

import type { ChannelTypes } from 'types';
import type { ReportingMetaInfoIGStorySnippetType, ReportingSummaryInfluencersMap } from 'modules/reporting/types';
import type {
  ContentCardContext as ContentCardContextAlias,
  ContentCardItem
} from 'components/cards/content-card/types';

applyTranslations(require('modules/reporting/components/content-cards/shared/content-cards-shared.i18n.json'));

type Props = {
  posts: Array<ContentCardItem>,
  channel: ChannelTypes,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType,
  selectedCreatorId?: number,
  influencersMapById: ReportingSummaryInfluencersMap,
  onNoPostClick?: () => void
};

const DEFAULT_NUM_TO_SHOW = 8;

const SHARED_MSG_KEY = 'content_cards_shared';

const ContentCards = ({
  posts,
  influencersMapById,
  channel,
  igStorySnippetType,
  selectedCreatorId,
  onNoPostClick
}: Props): JSX.Element => {
  const [showAll, setShowAll] = React.useState(false);
  const containerRef = React.createRef<HTMLDivElement>();

  const handleShowAll = (): void => {
    setShowAll(!showAll);

    if (showAll && containerRef.current) {
      containerRef.current.scrollIntoView();
    }
  };

  const filteredItems = selectedCreatorId
    ? posts.filter(item => item.influencerProfileId === selectedCreatorId)
    : posts;

  const itemsToShow = showAll ? filteredItems : filteredItems.slice(0, DEFAULT_NUM_TO_SHOW);
  const showButton = (!selectedCreatorId && posts.length > DEFAULT_NUM_TO_SHOW)
    || itemsToShow.length > DEFAULT_NUM_TO_SHOW;

  const context = React.useMemo<ContentCardContextAlias>(() => ({
    posts: filteredItems
  }), [posts]);

  return (
    <ContentCardContext.Provider value={context}>
      <div
        ref={containerRef}
        className="content-cards"
      >
        <div className="content-cards__items">
          {
            itemsToShow.map((item, index) => (
              <ContentCard
                key={`content-cards--${item.id}`}
                item={item}
                index={index}
                channel={channel}
                igStorySnippetType={igStorySnippetType}
                onNoPostClick={onNoPostClick}
                creatorProfile={influencersMapById[item.influencerProfileId]}
                onShowDetailsClick={() => (
                  addUserAction({
                    eventAction: 'show details',
                    eventCategory: 'content card',
                    customDimensionData: {
                      influencerId: item.influencerProfileId
                    }
                  })
                )}
              />
            ))
          }
        </div>
        {
          showButton && (
            <div className="content-cards__button">
              <ActionButton
                onClick={handleShowAll}
              >
                {__t(`${SHARED_MSG_KEY}.buttons.show_${showAll ? 'less' : 'more'}`)}
              </ActionButton>
            </div>
          )
        }
      </div>
    </ContentCardContext.Provider>
  );
};

export default ContentCards;
